import { useTranslation } from "react-i18next";
import { Bug, Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * «¿CUÁL DE ESTAS DIJISTE?» — la plaga dicha que encaja en varias (plan cultivos SIEX F7,
 * 05-10-2026). Con la IA guiada por cultivo, `voice-create-scouting` ya no coge «la más
 * parecida»: si lo dicho («trips») vale para varias plagas de la empresa, devuelve hasta 3
 * (primero las de la lista del cultivo del sitio) y SIN elegir ninguna. Quien clasifica la
 * elige aquí con un toque; si no elige, el parte va sin plaga («No lo sé»), como siempre.
 * Al trabajador no se le enseña: su voz sigue siendo solo una sugerencia (F21).
 */
export function VoicePestChoice({
  spoken,
  options,
  selectedId,
  onPick,
}: {
  spoken: string;
  options: Array<{ id: string; name: string }>;
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (options.length < 2) return null;
  return (
    <div className="space-y-2 rounded-2xl border border-[#f3dcb6] bg-[#fffaf1] px-3 py-2.5" data-testid="voice-pest-choice">
      <p className="flex items-center gap-1.5 text-sm font-bold text-[#7a4a06]">
        <Bug className="h-4 w-4 shrink-0 text-[#E0962E]" aria-hidden />
        {t("worker.incident.voicePestChoice.title", "¿Cuál de estas dijiste?")}
      </p>
      <p className="text-xs text-[#8a5a12]">
        {t("worker.incident.voicePestChoice.hint", { spoken, defaultValue: "«{{spoken}}» puede ser más de una plaga: toca la que es." })}
      </p>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("worker.incident.voicePestChoice.title", "¿Cuál de estas dijiste?")}>
        {options.map((o) => {
          const activa = selectedId === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onPick(o.id)}
              aria-pressed={activa}
              data-testid="voice-pest-choice-option"
              data-active={activa ? "true" : "false"}
              className={cn(
                "inline-flex min-h-[38px] max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-transform active:scale-95",
                activa ? "border-[#E0962E] bg-[#E0962E] text-white" : "border-[#f0d2a2] bg-white text-[#5c3a07]",
              )}
            >
              {activa && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              <span className="min-w-0 truncate">{o.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default VoicePestChoice;
